import grpcClient from '~/grpc/grpc-client'
import {
  GetRefundInfoRequest,
  GetRefundInfoResponse,
  PreferRefundRequest
} from '~/stub/gw/servicepr/refund_pb'
import { RefundService } from '~/stub/gw/servicepr/refund_pb_service'
import GrpcAccessorBase from '../grpc-accessor-base'
import { Stripped, toInt32Value, toStringValue } from '~/grpc/grpc-util'
import {
  GetDashboardCsvRequest,
  GetDashboardCsvResponse,
  GetDashboardRequest,
  GetDashboardResponse,
  ScreeningInfo
} from '~/stub/gw/servicepr/dashboard_pb'
import { DashboardService } from '~/stub/gw/servicepr/dashboard_pb_service'
import { StopRequest, PublishRequest } from '~/stub/gw/servicepr/service_pr_pb'
import { InitializeRequest, ChangeDailyBudgetRequest } from '~/stub/gw/servicepr/service_pr_pb'
import { ServicePrService } from '~/stub/gw/servicepr/service_pr_pb_service'
import { CreditCard } from '~/stub/gw/servicepr/service_pr_pb'
import { ChargeRequest, ChargeResponse } from '~/stub/gw/servicepr/charge_pb'
import { ServicePrChargeService } from '~/stub/gw/servicepr/charge_pb_service'
import { ServicePrPaymentType } from '~/stub/domain/enumeration/service_pr_payment_type_pb'
import { ImpressionService } from '~/stub/gw/servicepr/impression_pb_service'
import { SaveImpressionRequest, DisplayServicePr } from '~/stub/gw/servicepr/impression_pb'
import { SearchServiceResult, SearchServiceCondition } from '~/stub/apigateway/service/search_pb'
import {
  GetMonthlyPaymentHistoryRequest,
  GetMonthlyPaymentHistoryResponse,
  MonthlyPaymentHistory
} from '~/stub/gw/servicepr/monthly_payment_history_pb'
import { ServicePrMonthlyPaymentHistoryService } from '~/stub/gw/servicepr/monthly_payment_history_pb_service'
import { GetReceiptRequest, GetReceiptResponse } from '~/stub/gw/servicepr/receipt_pb'
import { ServicePrReceiptService } from '~/stub/gw/servicepr/receipt_pb_service'

export type DashboardParam = Stripped<GetDashboardRequest.AsObject>
export type DashboardResponseType = Stripped<GetDashboardResponse.AsObject>
export type RefundResponse = Stripped<GetRefundInfoResponse.AsObject>
export type ChargeRequestType = Stripped<ChargeRequest.AsObject>
export type InitialRequestType = Partial<Stripped<InitializeRequest.AsObject>>
export type ScreeningInfoType = Stripped<ScreeningInfo.AsObject>
export type SaveImpressionRequestType = Stripped<SaveImpressionRequest.AsObject>
export type ChangeDailyBudgetRequestType = Stripped<ChangeDailyBudgetRequest.AsObject>
export type DownloadCsvRequestType = Stripped<GetDashboardCsvRequest.AsObject>
export type SearchServiceResultType = SearchServiceResult.AsObject & { onlyPrServiceIndex: number }
export type SearchServiceConditionType = SearchServiceCondition.AsObject
export type PaymentHistoryParam = Stripped<GetMonthlyPaymentHistoryRequest.AsObject>
export type PaymentHistoryResponseType = Stripped<GetMonthlyPaymentHistoryResponse.AsObject>
export type ReceiptParam = Stripped<GetReceiptRequest.AsObject>
export type ReceiptResponseType = Stripped<GetReceiptResponse.AsObject>

/**
 * サービスPRアクセサ
 */
class ServicePrAccessor extends GrpcAccessorBase {
  /** ダッシュボード情報を取得する */
  fetchDashboard(params: DashboardParam): Promise<DashboardResponseType> {
    const metadata = this.getMetadata()

    const request = new GetDashboardRequest()
    request.setStartDate(toStringValue(params.startDate))
    request.setEndDate(toStringValue(params.endDate))

    return grpcClient({
      method: DashboardService.GetDashboard,
      request,
      metadata,
      strip: true
    })
  }

  /** サービスを広告配信する */
  publishService(params: Stripped<PublishRequest.AsObject>) {
    const metadata = this.getMetadata()

    const request = new PublishRequest()
    request.setServiceId(toInt32Value(params.serviceId))

    return grpcClient({
      method: ServicePrService.Publish,
      request,
      metadata
    })
  }

  /** サービスを広告配信を停止する */
  stopService(params: Stripped<StopRequest.AsObject>) {
    const metadata = this.getMetadata()

    const request = new StopRequest()
    request.setServiceId(toInt32Value(params.serviceId))

    return grpcClient({
      method: ServicePrService.Stop,
      request,
      metadata
    })
  }

  /** 返金情報を取得する */
  fetchRefund(): Promise<RefundResponse> {
    const metadata = this.getMetadata()
    const request = new GetRefundInfoRequest()

    return grpcClient({
      method: RefundService.GetRefundInfo,
      request,
      metadata,
      strip: true
    })
  }

  /** 返金申請を行う */
  preferRefund() {
    const metadata = this.getMetadata()
    const request = new PreferRefundRequest()

    return grpcClient({
      method: RefundService.PreferRefund,
      request,
      metadata
    })
  }

  /** チャージを行う */
  charge(params: ChargeRequestType): Promise<Stripped<ChargeResponse.AsObject>> {
    const metadata = this.getMetadata()
    const request = new ChargeRequest()
    request.setAmount(toInt32Value(params.amount))
    request.setCardSeq(toInt32Value(params.cardSeq))
    request.setDevicePrint(toStringValue(params.devicePrint))
    request.setIpAddress(toStringValue(params.ipAddress))

    return grpcClient({
      method: ServicePrChargeService.Charge,
      request,
      metadata,
      strip: true
    })
  }

  /** 日予算を設定する */
  changeDailyBudget(params: ChangeDailyBudgetRequestType) {
    const metadata = this.getMetadata()

    const request = new ChangeDailyBudgetRequest()
    request.setBudget(toInt32Value(params.budget))

    return grpcClient({
      method: ServicePrService.ChangeDailyBudget,
      request,
      metadata
    })
  }

  /** 初回設定方法を設定する */
  initializePayment(params: InitialRequestType) {
    const metadata = this.getMetadata()

    const request = new InitializeRequest()
    request.setDailyBudget(toInt32Value(params.dailyBudget))
    // 2022.04.06時点: クレジットカード決済のみなので固定設定
    request.setPaymentType(ServicePrPaymentType.SERVICE_PR_PAYMENT_TYPE_CHARGE)
    request.setChargeAmount(toInt32Value(params.chargeAmount))

    const card = new CreditCard()
    card.setHolderName(toStringValue(params.creditCard.holderName))
    card.setDevicePrint(toStringValue(params.creditCard.devicePrint))
    card.setIpAddress(toStringValue(params.creditCard.ipAddress))
    card.setToken(toStringValue(params.creditCard.token))
    request.setCreditCard(card)

    return grpcClient({
      method: ServicePrService.Initialize,
      request,
      metadata
    })
  }

  initializePaymentHasCreditCard(params: InitialRequestType) {
    const metadata = this.getMetadata()
    const request = new InitializeRequest()
    request.setDailyBudget(toInt32Value(params.dailyBudget))
    // 2022.04.06時点: クレジットカード決済のみなので固定設定
    request.setPaymentType(ServicePrPaymentType.SERVICE_PR_PAYMENT_TYPE_CHARGE)
    request.setChargeAmount(toInt32Value(params.chargeAmount))

    const card = new CreditCard()
    // 2022.04.06時点: クレジットカードは１枚のみ登録可能なので固定設定
    card.setCardSeq(toInt32Value(0))
    card.setDevicePrint(toStringValue(params.creditCard.devicePrint))
    card.setIpAddress(toStringValue(params.creditCard.ipAddress))
    request.setCreditCard(card)

    return grpcClient({
      method: ServicePrService.Initialize,
      request,
      metadata
    })
  }

  impressionPrService(
    params: Array<SearchServiceResultType>,
    conditions: SearchServiceConditionType,
    maxCount: number,
    realCount: number
  ) {
    const metadata = this.getMetadata()
    const request = new SaveImpressionRequest()

    const requestParams = params.map(searchResult => {
      const displayServicePr = new DisplayServicePr()
      displayServicePr.setSprKey(toStringValue(searchResult.servicePr.sprKey))
      displayServicePr.setServiceId(toInt32Value(searchResult.service.id))
      displayServicePr.setServicePrUuid(toStringValue(searchResult.servicePr.servicePrUuid))
      // 0始まりで採番されているが、APIでくるのは1始まり
      displayServicePr.setPrDisplayNo(toInt32Value(searchResult.onlyPrServiceIndex + 1))
      displayServicePr.setPage(toInt32Value(conditions.page ? Number(conditions.page) : 1))
      return displayServicePr
    })

    request.setServicePrsList(requestParams)
    request.setLayout(toStringValue(conditions.layout))
    request.setMasterCategoryId(toInt32Value(conditions.masterCategoryId))
    request.setKeyword(toStringValue(conditions.keyword))
    request.setServicePrMaxCount(toInt32Value(maxCount))
    request.setServicePrRealCount(toInt32Value(realCount))

    return grpcClient({
      method: ImpressionService.SaveImpression,
      request,
      metadata
    })
  }

  downloadCsv(params: DownloadCsvRequestType): Promise<Stripped<GetDashboardCsvResponse.AsObject>> {
    const metadata = this.getMetadata()
    const request = new GetDashboardCsvRequest()
    request.setStartDate(toStringValue(params.startDate))
    request.setEndDate(toStringValue(params.endDate))

    return grpcClient({
      method: DashboardService.GetDashboardCsv,
      request,
      metadata,
      strip: true
    })
  }

  /** 支払い履歴情報を取得する */
  fetchPaymentHistory(params: PaymentHistoryParam) {
    const metadata = this.getMetadata()

    const request = new GetMonthlyPaymentHistoryRequest()
    request.setYear(toInt32Value(params.year))

    return grpcClient({
      method: ServicePrMonthlyPaymentHistoryService.GetMonthlyPaymentHistory,
      request,
      metadata,
      strip: true
    })
  }

  /** 領収書情報を取得する */
  fetchReceipt(params: ReceiptParam) {
    const metadata = this.getMetadata()

    const request = new GetReceiptRequest()
    request.setYear(toInt32Value(params.year))
    request.setMonth(toInt32Value(params.month))

    return grpcClient({
      method: ServicePrReceiptService.GetReceipt,
      request,
      metadata,
      strip: true
    })
  }
}

export default ServicePrAccessor
